import {
  useMfaServiceMfaConfigsPartialUpdate,
  useMfaServiceMfaUsersListKey
} from '@organization/api/queries'
import { MFAConfig, MFAUser } from '@organization/api/requests'
import { showApiErrors } from '@shared/utils'
import { useQueryClient } from '@tanstack/react-query'
import { message, Modal, Table, TableColumnsType, TableProps } from 'antd'
import { SorterResult } from 'antd/es/table/interface'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { getConfigMFATableColumns } from './tableColumns'
import { ConfigMFAForm } from '../ConfigMFAForm'

const FORM_NAME = 'config-mfa-form'

type Props = {
  data?: MFAUser[]
  triggerSort: (sorter: SorterResult<MFAUser>) => void
  getSortedTableColumns: (
    columns: TableColumnsType<MFAUser>
  ) => TableColumnsType<MFAUser>
} & TableProps<MFAUser>

export const ConfigMFATable = ({
  data,
  triggerSort,
  getSortedTableColumns,
  ...props
}: Props) => {
  const { t } = useTranslation()
  const queryClient = useQueryClient()

  const [editModalId, setEditModalId] = useState<string>()

  const updateMutation = useMfaServiceMfaConfigsPartialUpdate({
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [useMfaServiceMfaUsersListKey]
      })
      message.success(t('edited_successfully'))
      onCloseEditModal()
    },
    onError: (error) => showApiErrors(error)
  })

  const columns = getSortedTableColumns(
    getConfigMFATableColumns(setEditModalId, t)
  )

  const onCloseEditModal = () => setEditModalId(undefined)

  const onFinish = (values: MFAConfig) => {
    updateMutation.mutate({
      id: editModalId as string,
      requestBody: values
    })
  }

  return (
    <>
      <Table
        columns={columns}
        dataSource={data}
        rowKey="id"
        scroll={{ x: 'max-content' }}
        onChange={(_, __, sorter) =>
          triggerSort(Array.isArray(sorter) ? sorter[0] : sorter)
        }
        {...props}
      />

      <Modal
        title={t('edit_backup_codes_mfa_user')}
        open={!!editModalId}
        onCancel={onCloseEditModal}
        okText={t('button.save')}
        okButtonProps={{
          form: FORM_NAME,
          htmlType: 'submit'
        }}
      >
        <ConfigMFAForm
          dataId={editModalId}
          name={FORM_NAME}
          onFinish={onFinish}
        />
      </Modal>
    </>
  )
}
